import { getSessionContext, type ActionSessionContext } from "@/app/actions";

import { useCartStore } from "./cart/cart-store";
import { useConsultantStore } from "./consultant/consultant-store";
import { usePartyStore } from "./party/party-store";
import { useSessionStore } from "./session/session-store";
import { useUserStore } from "./user/user-store";

/**
 * Initialize stores from server-fetched session context
 * This is the preferred method - uses data fetched on the server
 */
export function initializeStoreFromContext(
	context: ActionSessionContext
): void {
	useSessionStore.getState().setSessionState({
		sessionId: context.sessionId,
		token: context.token,
		locale: context.locale
	});

	initializeUserStore(context);
	initializeCartStore(context);
	initializePartyStore(context);
	initializeConsultantStore(context);
}

/**
 * Initialize stores by fetching session context client-side
 * @deprecated Use initializeStoreFromContext with server-fetched data instead
 * This method is kept for backward compatibility but should be avoided
 */
export async function initializeStore(sessionId: string): Promise<void> {
	const result = await getSessionContext(sessionId);

	if (result.type === "session-expired") {
		return;
	}

	initializeStoreFromContext(result.sessionContext);
}

function initializeUserStore(context: ActionSessionContext): void {
	useUserStore.getState().setUserState(context.user);
	useUserStore.getState().setPartyHost(context.party);
}

function initializeCartStore(context: ActionSessionContext): void {
	try {
		useCartStore.getState().setCartState(context.cart);
		if (context.cart.status === "not-connected") {
			console.error(`Cart not found for session ${context.sessionId}.`);
		}
	} catch (e) {
		console.error(
			`Failed to get cart for session: ${context.sessionId}.`,
			e
		);
	}
}

function initializePartyStore(context: ActionSessionContext): void {
	usePartyStore.getState().setPartyState(context.party);
}

function initializeConsultantStore(context: ActionSessionContext): void {
	useConsultantStore.getState().setConsultantState(context.consultant);
}
